import APIService from "./APIService";

/**
 * TTSService — Text-to-Speech abstraction layer.
 *
 * Supports two providers controlled by the REACT_APP_TTS_PROVIDER env variable:
 *   "browser"  — Web Speech API (free, works offline, robotic voices)
 *   "polly"    — AWS Polly via backend endpoint (neural voices, higher quality)
 *
 * Default: "browser"
 */

const TTS_PROVIDER = (
  process.env.REACT_APP_TTS_PROVIDER || "browser"
).toLowerCase();

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Strip markdown / HTML so the speech engine receives clean text.
 */
const cleanTextForSpeech = (text) => {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/<[^>]*>/g, "")
    .replace(/^[-*•]\s+/gm, "")
    .replace(/#{1,6}\s+/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\n{2,}/g, ". ")
    .replace(/\n/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
};

// ─── Browser (Web Speech API) provider ────────────────────────────────────────

let currentAudio = null; // Tracks the Audio object for Polly playback

const browserSpeak = (text, { onStart, onEnd, onError } = {}) => {
  if (!window.speechSynthesis) {
    onError?.("SpeechSynthesis not supported");
    return;
  }
  window.speechSynthesis.cancel();

  const clean = cleanTextForSpeech(text);
  if (!clean) return;

  const utterance = new SpeechSynthesisUtterance(clean);
  utterance.rate = 0.95;
  utterance.pitch = 1;
  utterance.lang = "en-US";

  utterance.onstart = () => onStart?.();
  utterance.onend = () => onEnd?.();
  utterance.onerror = () => onError?.("SpeechSynthesis error");

  window.speechSynthesis.speak(utterance);
};

const browserStop = () => {
  window.speechSynthesis?.cancel();
};

// ─── AWS Polly provider (via backend) ─────────────────────────────────────────

const pollySpeak = async (text, { onStart, onEnd, onError } = {}) => {
  pollyStop(); // stop any previous playback

  const clean = cleanTextForSpeech(text);
  if (!clean) return;

  try {
    onStart?.();

    const response = await APIService.getRaw(
      `/api/v1/app/tts/synthesize?text=${encodeURIComponent(clean)}`,
    );

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    currentAudio = new Audio(url);
    currentAudio.onended = () => {
      URL.revokeObjectURL(url);
      currentAudio = null;
      onEnd?.();
    };
    currentAudio.onerror = () => {
      URL.revokeObjectURL(url);
      currentAudio = null;
      onError?.("Audio playback error");
    };
    currentAudio.play();
  } catch (err) {
    console.error(
      "[TTSService] Polly error, falling back to browser TTS:",
      err,
    );
    // Graceful fallback to browser TTS if Polly fails
    browserSpeak(text, { onStart, onEnd, onError });
  }
};

const pollyStop = () => {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
};

// ─── Public API ───────────────────────────────────────────────────────────────

const TTSService = {
  /**
   * The active provider name ("browser" or "polly").
   */
  provider: TTS_PROVIDER,

  /**
   * Speak the given text.
   * @param {string} text   — raw message text (may contain markdown)
   * @param {object} opts   — { onStart, onEnd, onError } callbacks
   */
  speak: (text, opts = {}) => {
    if (TTS_PROVIDER === "polly") {
      pollySpeak(text, opts);
    } else {
      browserSpeak(text, opts);
    }
  },

  /**
   * Stop any ongoing speech.
   */
  stop: () => {
    if (TTS_PROVIDER === "polly") {
      pollyStop();
    }
    // Always cancel browser synthesis too (in case of fallback)
    browserStop();
  },

  /**
   * Whether the browser supports TTS at all (either provider).
   */
  isSupported: () => {
    if (TTS_PROVIDER === "polly") return true; // Polly uses Audio which is universally supported
    return !!window.speechSynthesis;
  },
};

export default TTSService;
